/**
 * Program & module outline API (tenant-scoped).
 */
import { sharedApiClient, type PostFormDataOptions } from './client';
import type { ContentTagRef } from './content.service';

export type ProgramScope = 'ai' | 'document';

/** 0 = manual, 1 = AI-generated; API field name is ``type``. */
export type ProgramType = 0 | 1;

export const PROGRAM_TYPE_MANUAL: ProgramType = 0;
export const PROGRAM_TYPE_AI_GENERATE: ProgramType = 1;

export type ProgramStatusLabel = 'draft' | 'published' | 'archived';

export interface ProgramListItem {
    id: string;
    code: string;
    name: string;
    tenant_id?: string;
    tenant_name?: string;
    // EAA-1648: Program owner info
    user_id?: number | null;
    user_username?: string | null;
    objective: string;
    number_modules: number;
    actual_module_count: number;
    scope: ProgramScope;
    type: ProgramType;
    status: ProgramStatusLabel;
    created_at: string;
    updated_at: string;
}

export interface ProgramListParams {
    tenant_id?: string;
    page?: number;
    page_size?: number;
    search?: string;
    scope?: ProgramScope;
    status?: string;
    ordering?: string;
}

export interface ProgramListResponse {
    results: ProgramListItem[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

export interface ProgramTagItem {
    id: string;
    name: string;
    code: string;
    /** Present on many API payloads (module tree / tag detail). */
    tag_type_id?: string;
    tag_type?: {
        id: string;
        name: string;
        code: string;
        is_system?: boolean;
        is_inherited?: boolean;
        is_required?: boolean;
        selection_mode?: 'single' | 'multiple';
    };
}

/**
 * EAA-2027: an attached program document. There is deliberately no `url`: the file is
 * private, so the API never returns its storage link. Open it with
 * `programService.getDocumentContent`, which the server answers only for an authenticated
 * caller with access to the program.
 */
export interface ProgramDocumentItem {
    id: string;
    name: string;
    file_type: string;
    size: number | null;
    created_at: string;
    updated_at: string;
}

export interface UserItem {
    id: string;
    username: string;
}

export interface EnrollPayload {
    user_id?: string;
    user_ids?: string[];
    action?: 'unenroll';
}

export interface ProgramDetail {
    id: string;
    code: string;
    name: string;
    objective: string;
    number_modules: number;
    actual_module_count: number;
    scope: ProgramScope;
    type: ProgramType;
    status: ProgramStatusLabel;
    tags: ProgramTagItem[];
    documents: ProgramDocumentItem[];
    /** Django ``User.id`` (integer PK) of the program owner; null when unowned. */
    user_id?: number | null;
    user_username?: string | null;
    enrolled_users?: UserItem[];
    created_at: string;
    updated_at: string;
}

export interface CreateProgramPayload {
    code?: string;
    name: string;
    objective?: string;
    number_modules?: number;
    scope: ProgramScope;
    /** Defaults to 0 (manual) on the server if omitted. */
    type?: ProgramType;
    status?: ProgramStatusLabel;
    tag_ids?: string[];
    documents?: ProgramDocumentPayload[];
}

export interface ProgramDocumentPayload {
    name: string;
    /** Set after S3 upload (legacy) or omitted when sending ``file`` with generate. */
    url?: string;
    /** Staged local file for multipart ``POST .../programs/generate/``. */
    file?: File;
    file_type?: string;
    size?: number | null;
}

export interface UpdateProgramPayload {
    code?: string;
    name?: string;
    objective?: string;
    number_modules?: number;
    scope?: ProgramScope;
    type?: ProgramType;
    status?: ProgramStatusLabel;
    tag_ids?: string[];
}

export interface TagForAIEntry {
    code: string;
    name: string;
}

export interface TagForAIGroup {
    group: string;
    tags: TagForAIEntry[];
}

export interface GenerateProgramRequest {
    course_name: string;
    code?: string;
    objectives?: string;
    num_units: number;
    knowledge_scope: 'focus_doc' | 'general';
    /** Sent for traceability; server sets program type to AI on create. */
    type?: ProgramType;
    tag_ids?: string[];
    documents?: ProgramDocumentPayload[];
    tags_for_ai?: TagForAIGroup[];
    webhook_url?: string;
}

export interface GenerateProgramResponse {
    job_id: string;
    program_id: string;
    status: string;
}

/** POST /program/programs/suggest-tags/ (202 — poll job for result.tag_ids) */
export interface SuggestProgramTagsRequest {
    tag_type_ids: string[];
    documents?: { url: string; name?: string }[];
    /** EAA-2027: an attached program document, resolved (and access-checked) server-side. */
    program_document_id?: string;
    webhook_url?: string;
}

export interface SuggestProgramTagsResponse {
    job_id: string;
    status: string;
}

/**
 * EAA-2063: one AI question type a textbook activity can become. ``slug`` is the agent's
 * question type; ``content_type_slug`` is the AIContentType slug sent as ``question_type``
 * to POST /content/bulk-generate/.
 */
export interface TextbookActivityCandidate {
    slug: string;
    content_type_slug: string;
    needs_audio: boolean;
    needs_image: boolean;
}

/** EAA-2063: one exercise found in the textbook section linked to a lesson. */
export interface TextbookActivity {
    ref: string;
    /** Page number printed in the book, when known. */
    page: string | null;
    /** 1-based page in the PDF. */
    pdf_page: number;
    /** Exercise number printed in the book, when known. */
    number: number | null;
    instruction: string;
    kind: string;
    /** First lines of the exercise, for a preview. */
    preview: string[];
    item_count: number;
    has_audio: boolean;
    has_image: boolean;
    /** Question types it can become, best match first. */
    candidates: TextbookActivityCandidate[];
}

export type TextbookLessonStatus =
    | 'ready'
    | 'not_extracted'
    /** EAA-2063: the book is being read (an extract job is running). */
    | 'extracting'
    /** EAA-2063: the last extract job for the book failed. */
    | 'extract_failed'
    | 'no_section'
    | 'no_document'
    | 'error';

export interface TextbookLessonActivities {
    module_id: string;
    module_title: string;
    status: TextbookLessonStatus;
    activities?: TextbookActivity[];
    /** ``lesson_content`` when the section has no exercises and AI should practise the lesson. */
    fallback?: 'lesson_content' | null;
    fallback_types?: TextbookActivityCandidate[];
}

/** POST /program/programs/textbook-activities/ */
export interface TextbookActivitiesRequest {
    module_ids: string[];
}

export interface TextbookActivitiesResponse {
    lessons: TextbookLessonActivities[];
}

/** POST /program/programs/textbook-extract/ — read the books linked to these lessons. */
export interface TextbookExtractRequest {
    module_ids: string[];
}

export interface TextbookExtractDocument {
    document_id: string;
    status: 'started' | 'already_ready' | 'error';
    job_id?: string;
}

export interface TextbookExtractResponse {
    documents: TextbookExtractDocument[];
}

export interface RegenerateProgramRequest {
    prompts: string[];
    webhook_url?: string;
}

export interface ModuleLevelNested {
    id: string;
    code: string;
    label: string;
    order: number;
    status: string;
}

export interface ModuleContentItem {
    id: string;
    content_id: string;
    content_title: string;
    content_meta_id: string;
    order: number;
    /** Pinned content publish version. null = always use latest. */
    publish_version: number | null;
    created_at: string;
    tags?: ContentTagRef[];
    /** EAA-2027: program document the AI generation was grounded in (name only). */
    source_document_name?: string | null;
    /** EAA-2061: section of that document the generation drew on, e.g. "Unit 1 Hello › Lesson 2". */
    source_section?: string | null;
    /** EAA-2061: pages of that document the generation drew on. */
    source_pages?: SourcePages | null;
    /** EAA-2061: full provenance — the paragraphs and pictures the generation used. */
    source_provenance?: SourceProvenance | null;
}

/** EAA-2061: `printed` = the number printed on the book page; `pdf` = the PDF page index. */
export type SourcePageNumbering = 'printed' | 'pdf';

export interface SourcePages {
    start: number;
    end: number;
    numbering?: SourcePageNumbering;
}

export interface SourceEvidence {
    id: string;
    section_path: string | null;
    page_start: number | null;
    page_end: number | null;
    text: string;
}

export interface SourceReferenceImage {
    image_url: string;
    page: number | null;
}

export interface SourceProvenance {
    document_name: string | null;
    section_path: string | null;
    pages: { start: number; end: number } | null;
    page_numbering: SourcePageNumbering;
    evidence: SourceEvidence[];
    reference_images: SourceReferenceImage[];
}

/** Folder node within a module's content area (recursive). */
export interface ContentGroupNode {
    id: string;
    name: string;
    description: string;
    /** Module id (root groups only); null for nested groups. */
    module: string | null;
    /** Parent group id (nested groups only); null for root groups. */
    parent: string | null;
    order: number;
    contents: ModuleContentItem[];
    children: ContentGroupNode[];
    created_at: string;
    updated_at: string;
}

export interface ModuleTreeNode {
    id: string;
    program: string;
    parent: string | null;
    level: ModuleLevelNested;
    title: string;
    description: string;
    /** EAA-1399: optional cover/metadata image URL for the module level. */
    image_url?: string;
    order: number;
    status: ProgramStatusLabel;
    tags: ProgramTagItem[];
    /**
     * Module-direct content rows. In lightweight mode (getModuleTree with
     * includeContents:false) this arrives empty and is lazy-loaded page-by-page
     * via getModuleContents — see EAA-1399. `content_count` still reflects the
     * true total for the count badge / "has content" checks.
     */
    contents: ModuleContentItem[];
    /** EAA-1399: total module-direct content count (present even in lightweight mode). */
    content_count?: number;
    /** Folder subtrees anchored to this module. */
    groups: ContentGroupNode[];
    children: ModuleTreeNode[];
    created_at: string;
    updated_at: string;
    /**
     * EAA-1399: client-only marker — true for a module created locally that has
     * not yet been persisted (no server id). Server responses never set this.
     * Used so the outline save carries content only for genuinely-new modules and
     * preserves (rather than rebuilds) the content of server-persisted ones, which
     * may be lazy-loaded / partially in memory.
     */
    _local?: boolean;
    /**
     * EAA-1735: client-only marker — true once a server-persisted module's
     * direct content was locally added to/removed from/reordered (content bank,
     * import, drag reorder) rather than persisted immediately. Only set after
     * the module's content has been fully paged in, so `contents` is a complete,
     * safe-to-serialize set. Lets outline save send the real content for this
     * module instead of `preserve_contents`. Server responses never set this.
     */
    _contentDirty?: boolean;
}

/** One page of a module's content (EAA-1399 lazy loading). */
export interface ModuleContentPage {
    results: ModuleContentItem[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

export interface CreateModulePayload {
    program: string;
    parent?: string | null;
    level_id: string;
    title: string;
    description?: string;
    /** EAA-1399: optional metadata image URL (from the media upload endpoint). */
    image_url?: string;
    order?: number;
    status?: ProgramStatusLabel;
    tag_ids?: string[];
}

export interface UpdateModulePayload {
    parent?: string | null;
    level_id?: string;
    title?: string;
    description?: string;
    /** EAA-1399: optional metadata image URL; empty string clears it. */
    image_url?: string;
    order?: number;
    status?: ProgramStatusLabel;
    tag_ids?: string[];
}

export interface ModuleLevelItem {
    id: string;
    code: string;
    label: string;
    order: number;
    status: string;
    created_at: string;
    updated_at: string;
}

/** Folder payload node within outline sync (recursive). */
export interface SyncGroupNode {
    client_id?: string;
    name: string;
    description?: string;
    order?: number;
    content_ids?: string[];
    /** Parallel to content_ids — actual order values for interleaved sorting. */
    content_orders?: number[];
    children?: SyncGroupNode[];
}

/** Payload node for POST .../programs/<id>/modules/sync/ (manual outline save). */
export interface SyncModuleNode {
    client_id?: string;
    level_id: string;
    title: string;
    description?: string;
    order?: number;
    tag_ids?: string[];
    content_ids?: string[];
    /** Parallel to content_ids — actual order values for interleaved sorting. */
    content_orders?: number[];
    /**
     * EAA-1399: keep the existing module's module-direct content instead of
     * rebuilding it from content_ids (set for server-persisted modules whose
     * content may be lazy-loaded). The backend restores it from a pre-delete
     * snapshot keyed by client_id.
     */
    preserve_contents?: boolean;
    /** Folder subtrees attached to this module. */
    groups?: SyncGroupNode[];
    children?: SyncModuleNode[];
}

export interface SyncOutlinePayload {
    modules: SyncModuleNode[];
}

/**
 * EAA-1738: one touched module in POST .../modules/sync-incremental/ (FLAT).
 * Hierarchy is expressed via `parent_client_id` so only changed modules are sent.
 * `client_id` = existing module id (update in place) or a local temp id (create).
 */
export interface IncrementalSyncModuleNode {
    client_id: string;
    parent_client_id: string | null;
    level_id: string;
    title: string;
    description?: string;
    order?: number;
    tag_ids?: string[];
    /** true → leave the module's existing module-direct content untouched. */
    preserve_contents?: boolean;
    content_ids?: string[];
    content_orders?: number[];
    /** true → delete + recreate this module's folders from `groups`. */
    rebuild_groups?: boolean;
    groups?: SyncGroupNode[];
}

export interface IncrementalSyncPayload {
    modules: IncrementalSyncModuleNode[];
    deleted_ids: string[];
}

export interface OutlineSyncResult {
    module_count: number;
}

/** Create a folder. Server enforces XOR: exactly one of module_id or parent_id. */
export interface CreateContentGroupPayload {
    /** Required for a root folder anchored to a module. */
    module_id?: string;
    /** Required for a nested folder under another folder. */
    parent_id?: string;
    name: string;
    description?: string;
    order?: number;
}

export interface UpdateContentGroupPayload {
    name?: string;
    description?: string;
    order?: number;
    /** Move within same module subtree only; root↔nested promotion rejected by server. */
    parent_id?: string | null;
}

function withTenantQuery(tenantId?: string): string {
    if (!tenantId) return '';
    const q = new URLSearchParams({ tenant_id: tenantId }).toString();
    return `?${q}`;
}

class ProgramService {
    private basePath = '/program';

    async listModuleLevels(tenantId?: string): Promise<ModuleLevelItem[]> {
        const queryParams = new URLSearchParams();
        if (tenantId) queryParams.append('tenant_id', tenantId);
        queryParams.append('status', 'active');
        queryParams.append('page_size', '100');
        queryParams.append('page', '1');
        const qs = queryParams.toString();
        const url = `${this.basePath}/levels/list/?${qs}`;
        const response = await sharedApiClient.get<{
            results: ModuleLevelItem[];
            count: number;
            page: number;
            page_size: number;
            total_pages: number;
        }>(url);
        return response.data.results;
    }

    async listPrograms(params?: ProgramListParams): Promise<ProgramListResponse> {
        const queryParams = new URLSearchParams();
        if (params?.tenant_id) queryParams.append('tenant_id', params.tenant_id);
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.page_size) queryParams.append('page_size', String(params.page_size));
        if (params?.search) queryParams.append('search', params.search);
        if (params?.scope) queryParams.append('scope', params.scope);
        if (params?.status) queryParams.append('status', params.status);
        if (params?.ordering) queryParams.append('ordering', params.ordering);
        const qs = queryParams.toString();
        const url = qs
            ? `${this.basePath}/programs/list/?${qs}`
            : `${this.basePath}/programs/list/`;
        const response = await sharedApiClient.get<ProgramListResponse>(url);
        return response.data;
    }

    async getProgram(programId: string, tenantId?: string): Promise<ProgramDetail> {
        const url = `${this.basePath}/programs/${programId}${withTenantQuery(tenantId)}`;
        const response = await sharedApiClient.get<ProgramDetail>(url);
        return response.data;
    }

    async createProgram(data: CreateProgramPayload, tenantId?: string): Promise<ProgramDetail> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const response = await sharedApiClient.post<ProgramDetail>(
            `${this.basePath}/programs/`,
            body
        );
        return response.data;
    }

    async updateProgram(
        programId: string,
        data: UpdateProgramPayload,
        tenantId?: string
    ): Promise<ProgramDetail> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const url = `${this.basePath}/programs/${programId}${withTenantQuery(tenantId)}`;
        const response = await sharedApiClient.put<ProgramDetail>(url, body);
        return response.data;
    }

    async deleteProgram(programId: string, tenantId?: string): Promise<void> {
        const url = `${this.basePath}/programs/${programId}${withTenantQuery(tenantId)}`;
        await sharedApiClient.delete(url);
    }

    async duplicateProgram(programId: string, tenantId?: string): Promise<ProgramDetail> {
        const qs = withTenantQuery(tenantId);
        const url = `${this.basePath}/programs/${programId}/duplicate/${qs}`;
        const response = await sharedApiClient.post<ProgramDetail>(url);
        return response.data;
    }

    async generateProgram(
        data: GenerateProgramRequest,
        tenantId?: string
    ): Promise<GenerateProgramResponse> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const response = await sharedApiClient.post<GenerateProgramResponse>(
            `${this.basePath}/programs/generate/`,
            body
        );
        return response.data;
    }

    /**
     * Queue AI curriculum generation with multipart ``file`` (preferred for focus_doc).
     * API uploads the file to storage, then the job downloads it for course-outline and links ProgramDocument after modules save.
     */
    async generateProgramMultipart(
        formData: FormData,
        options?: PostFormDataOptions
    ): Promise<GenerateProgramResponse> {
        const response = await sharedApiClient.postFormData<GenerateProgramResponse>(
            `${this.basePath}/programs/generate/`,
            formData,
            {
                timeoutMs: 600_000,
                ...options,
            }
        );
        return response.data;
    }

    /**
     * EAA-2027: the bytes of a private program document, fetched with the caller's auth.
     * GET /programs/{programId}/documents/{documentId}/content/ — there is no shareable
     * link; the server checks program access on every read.
     */
    async getDocumentContent(
        programId: string,
        documentId: string,
        tenantId?: string
    ): Promise<Blob> {
        return sharedApiClient.getBlob(
            `${this.basePath}/programs/${programId}/documents/${documentId}/content/${withTenantQuery(tenantId)}`
        );
    }

    /**
     * EAA-2063: the exercises in the textbook section of each lesson.
     * POST /program/programs/textbook-activities/ — the first call for a book can take
     * up to about a minute (the BE reads the pages), hence the long timeout.
     */
    async getTextbookActivities(
        data: TextbookActivitiesRequest,
        tenantId?: string
    ): Promise<TextbookActivitiesResponse> {
        const response = await sharedApiClient.post<TextbookActivitiesResponse>(
            `${this.basePath}/programs/textbook-activities/${withTenantQuery(tenantId)}`,
            data,
            { timeoutMs: 120_000 }
        );
        return response.data;
    }

    /**
     * EAA-2063: start reading (extracting) the textbooks linked to these lessons.
     * POST /program/programs/textbook-extract/ — returns at once; the lessons report
     * `extracting` in getTextbookActivities until the job ends.
     */
    async extractTextbooks(
        data: TextbookExtractRequest,
        tenantId?: string
    ): Promise<TextbookExtractResponse> {
        const response = await sharedApiClient.post<TextbookExtractResponse>(
            `${this.basePath}/programs/textbook-extract/${withTenantQuery(tenantId)}`,
            data
        );
        return response.data;
    }

    async suggestProgramTags(
        data: SuggestProgramTagsRequest,
        tenantId?: string
    ): Promise<SuggestProgramTagsResponse> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const response = await sharedApiClient.post<SuggestProgramTagsResponse>(
            `${this.basePath}/programs/suggest-tags/`,
            body
        );
        return response.data;
    }

    /**
     * Queue AI suggest-tags with multipart ``file`` (same flow as generate).
     * BE saves the file and stores in Redis/S3 for the worker.
     */
    async suggestProgramTagsMultipart(
        formData: FormData,
        options?: PostFormDataOptions
    ): Promise<SuggestProgramTagsResponse> {
        const response = await sharedApiClient.postFormData<SuggestProgramTagsResponse>(
            `${this.basePath}/programs/suggest-tags/`,
            formData,
            {
                timeoutMs: 600_000,
                ...options,
            }
        );
        return response.data;
    }

    async regenerateProgram(
        programId: string,
        data: RegenerateProgramRequest,
        tenantId?: string
    ): Promise<{ job_id: string; status: string }> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const response = await sharedApiClient.post<{ job_id: string; status: string }>(
            `${this.basePath}/programs/${programId}/regenerate/`,
            body
        );
        return response.data;
    }

    async publishProgram(programId: string, tenantId?: string): Promise<ProgramDetail> {
        let url = `${this.basePath}/programs/${programId}/publish/`;
        if (tenantId) {
            url = `${url}?tenant_id=${encodeURIComponent(tenantId)}`;
        }
        const response = await sharedApiClient.post<ProgramDetail>(url, {});
        return response.data;
    }

    async getModuleTree(
        programId: string,
        tenantId?: string,
        opts?: { includeContents?: boolean },
    ): Promise<ModuleTreeNode[]> {
        const params = new URLSearchParams();
        if (tenantId) params.set('tenant_id', tenantId);
        // EAA-1399: opt into lightweight mode so a 1000+ item module doesn't ship
        // inline; content is lazy-loaded via getModuleContents.
        if (opts?.includeContents === false) params.set('include_contents', 'false');
        const qs = params.toString();
        const url = `${this.basePath}/programs/${programId}/modules/tree/${qs ? `?${qs}` : ''}`;
        const response = await sharedApiClient.get<ModuleTreeNode[]>(url);
        return response.data;
    }

    /**
     * EAA-1399: fetch one page of a module's content links (lazy tree loading).
     * GET /programs/{programId}/modules/{moduleId}/contents/?page=&page_size=
     */
    async getModuleContents(
        programId: string,
        moduleId: string,
        opts: { page?: number; pageSize?: number; tenantId?: string } = {},
    ): Promise<ModuleContentPage> {
        const params = new URLSearchParams();
        if (opts.page) params.set('page', String(opts.page));
        if (opts.pageSize) params.set('page_size', String(opts.pageSize));
        if (opts.tenantId) params.set('tenant_id', opts.tenantId);
        const qs = params.toString();
        const url = `${this.basePath}/programs/${programId}/modules/${moduleId}/contents/${
            qs ? `?${qs}` : ''
        }`;
        const response = await sharedApiClient.get<ModuleContentPage>(url);
        return response.data;
    }

    /**
     * Replace the full module tree for a program in one request (transaction on server).
     */
    async syncOutline(
        programId: string,
        data: SyncOutlinePayload,
        tenantId?: string
    ): Promise<OutlineSyncResult> {
        const body: SyncOutlinePayload & { tenant_id?: string } = tenantId
            ? { ...data, tenant_id: tenantId }
            : { ...data };
        const response = await sharedApiClient.post<OutlineSyncResult>(
            `${this.basePath}/programs/${programId}/modules/sync/`,
            body
        );
        return response.data;
    }

    /**
     * EAA-1738: Persist only the touched modules (upsert + delete) instead of
     * replacing the whole tree. Untouched modules keep their rows/content/tags/
     * folders. Callers should fall back to `syncOutline` on failure.
     */
    async syncOutlineIncremental(
        programId: string,
        data: IncrementalSyncPayload,
        tenantId?: string
    ): Promise<OutlineSyncResult> {
        const body: IncrementalSyncPayload & { tenant_id?: string } = tenantId
            ? { ...data, tenant_id: tenantId }
            : { ...data };
        const response = await sharedApiClient.post<OutlineSyncResult>(
            `${this.basePath}/programs/${programId}/modules/sync-incremental/`,
            body
        );
        return response.data;
    }

    async createModule(data: CreateModulePayload, tenantId?: string): Promise<unknown> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const response = await sharedApiClient.post(`${this.basePath}/modules/`, body);
        return response.data;
    }

    async updateModule(
        moduleId: string,
        data: UpdateModulePayload,
        tenantId?: string
    ): Promise<unknown> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const url = `${this.basePath}/modules/${moduleId}${withTenantQuery(tenantId)}`;
        const response = await sharedApiClient.put(url, body);
        return response.data;
    }

    /** Module detail (tree node shape) for title/program resolution. */
    async getModule(moduleId: string, tenantId?: string): Promise<ModuleTreeNode> {
        const url = `${this.basePath}/modules/${moduleId}${withTenantQuery(tenantId)}`;
        const response = await sharedApiClient.get<ModuleTreeNode>(url);
        return response.data;
    }

    async deleteModule(moduleId: string, tenantId?: string): Promise<void> {
        const url = `${this.basePath}/modules/${moduleId}${withTenantQuery(tenantId)}`;
        await sharedApiClient.delete(url);
    }

    async reorderModules(moduleIds: string[], tenantId?: string): Promise<{ success: boolean }> {
        const body: { module_ids: string[]; tenant_id?: string } = { module_ids: moduleIds };
        if (tenantId) body.tenant_id = tenantId;
        const response = await sharedApiClient.post<{ success: boolean }>(
            `${this.basePath}/modules/reorder/`,
            body
        );
        return response.data;
    }

    async linkContents(
        moduleId: string,
        contentIds: string[],
        tenantId?: string
    ): Promise<unknown> {
        const body: { content_ids: string[]; tenant_id?: string } = { content_ids: contentIds };
        if (tenantId) body.tenant_id = tenantId;
        const response = await sharedApiClient.post(
            `${this.basePath}/modules/${moduleId}/contents/`,
            body
        );
        return response.data;
    }

    async unlinkContent(moduleId: string, contentId: string, tenantId?: string): Promise<void> {
        const url = `${this.basePath}/modules/${moduleId}/contents/${contentId}${withTenantQuery(tenantId)}`;
        await sharedApiClient.delete(url);
    }

    /**
     * Update the pinned publish_version on a module-content link.
     * @param publishVersion - version number to pin, or null for "Always Latest"
     */
    async updateContentPublishVersion(
        moduleId: string,
        contentId: string,
        publishVersion: number | null,
        tenantId?: string
    ): Promise<void> {
        const url = `${this.basePath}/modules/${moduleId}/contents/${contentId}${withTenantQuery(tenantId)}`;
        await sharedApiClient.patch(url, { publish_version: publishVersion });
    }

    async reorderModuleContents(
        moduleId: string,
        contentIds: string[],
        tenantId?: string
    ): Promise<{ success: boolean }> {
        const body: { content_ids: string[]; tenant_id?: string } = { content_ids: contentIds };
        if (tenantId) body.tenant_id = tenantId;
        const response = await sharedApiClient.put<{ success: boolean }>(
            `${this.basePath}/modules/${moduleId}/contents/reorder/`,
            body
        );
        return response.data;
    }

    // ── Content folders (groups) ────────────────────────────────────────────
    private contentBasePath = '/content';

    async getModuleGroupTree(
        moduleId: string,
        tenantId?: string
    ): Promise<ContentGroupNode[]> {
        const url = `${this.basePath}/modules/${moduleId}/groups/tree/${withTenantQuery(tenantId)}`;
        const response = await sharedApiClient.get<ContentGroupNode[]>(url);
        return response.data;
    }

    async createGroup(
        data: CreateContentGroupPayload,
        tenantId?: string
    ): Promise<ContentGroupNode> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const response = await sharedApiClient.post<ContentGroupNode>(
            `${this.contentBasePath}/groups/`,
            body
        );
        return response.data;
    }

    async getGroup(groupId: string, tenantId?: string): Promise<ContentGroupNode> {
        const url = `${this.contentBasePath}/groups/${groupId}${withTenantQuery(tenantId)}`;
        const response = await sharedApiClient.get<ContentGroupNode>(url);
        return response.data;
    }

    async updateGroup(
        groupId: string,
        data: UpdateContentGroupPayload,
        tenantId?: string
    ): Promise<ContentGroupNode> {
        const body = tenantId ? { ...data, tenant_id: tenantId } : data;
        const url = `${this.contentBasePath}/groups/${groupId}${withTenantQuery(tenantId)}`;
        const response = await sharedApiClient.patch<ContentGroupNode>(url, body);
        return response.data;
    }

    async deleteGroup(groupId: string, tenantId?: string): Promise<void> {
        const url = `${this.contentBasePath}/groups/${groupId}${withTenantQuery(tenantId)}`;
        await sharedApiClient.delete(url);
    }

    async linkGroupContents(
        groupId: string,
        contentIds: string[],
        tenantId?: string
    ): Promise<unknown> {
        const body: { content_ids: string[]; tenant_id?: string } = { content_ids: contentIds };
        if (tenantId) body.tenant_id = tenantId;
        const response = await sharedApiClient.post(
            `${this.contentBasePath}/groups/${groupId}/contents/`,
            body
        );
        return response.data;
    }

    async unlinkGroupContent(
        groupId: string,
        contentId: string,
        tenantId?: string
    ): Promise<void> {
        const url = `${this.contentBasePath}/groups/${groupId}/contents/${contentId}${withTenantQuery(tenantId)}`;
        await sharedApiClient.delete(url);
    }

    async reorderGroupContents(
        groupId: string,
        contentIds: string[],
        tenantId?: string
    ): Promise<{ success: boolean }> {
        const body: { content_ids: string[]; tenant_id?: string } = { content_ids: contentIds };
        if (tenantId) body.tenant_id = tenantId;
        const response = await sharedApiClient.post<{ success: boolean }>(
            `${this.contentBasePath}/groups/${groupId}/contents/reorder/`,
            body
        );
        return response.data;
    }

    async reorderGroups(
        groupIds: string[],
        tenantId?: string
    ): Promise<{ success: boolean }> {
        const body: { group_ids: string[]; tenant_id?: string } = { group_ids: groupIds };
        if (tenantId) body.tenant_id = tenantId;
        const response = await sharedApiClient.post<{ success: boolean }>(
            `${this.contentBasePath}/groups/reorder/`,
            body
        );
        return response.data;
    }

    // ── User Enrollment ────────────────────────────────────────────────────

    async enrollUser(
        programId: string,
        userId: string,
        tenantId?: string
    ): Promise<void> {
        const body: EnrollPayload & { tenant_id?: string } = { user_id: userId };
        if (tenantId) body.tenant_id = tenantId;
        await sharedApiClient.post(
            `${this.basePath}/programs/${programId}/enroll/`,
            body
        );
    }

    async bulkEnrollUsers(
        programId: string,
        userIds: string[],
        tenantId?: string
    ): Promise<void> {
        const body: EnrollPayload & { tenant_id?: string } = { user_ids: userIds };
        if (tenantId) body.tenant_id = tenantId;
        await sharedApiClient.post(
            `${this.basePath}/programs/${programId}/enroll/`,
            body
        );
    }

    async selfEnroll(programId: string, tenantId?: string): Promise<void> {
        const body: { tenant_id?: string } = {};
        if (tenantId) body.tenant_id = tenantId;
        await sharedApiClient.post(
            `${this.basePath}/programs/${programId}/enroll/`,
            body
        );
    }

    async unenrollUser(
        programId: string,
        userId: string,
        tenantId?: string
    ): Promise<void> {
        const body: EnrollPayload & { tenant_id?: string } = {
            user_id: userId,
            action: 'unenroll',
        };
        if (tenantId) body.tenant_id = tenantId;
        await sharedApiClient.post(
            `${this.basePath}/programs/${programId}/enroll/`,
            body
        );
    }
}

export const programService = new ProgramService();
